import React, { useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "../constants/Colors";
import { trpcReact } from "../trpc_client";
import { useAuth } from "../contexts/AuthContext";

export interface UploadedImage {
  uri: string;
  object_name: string;
  get_url: string;
  progress: number;
}

interface ImageUploaderProps {
  images: UploadedImage[];
  onImagesChange: (
    updater: UploadedImage[] | ((prev: UploadedImage[]) => UploadedImage[]),
  ) => void;
  onRemoveImage: (index: number) => void;
  kind: "profile" | "flat";
  minImages?: number;
  maxImages?: number;
  onAuthRequired?: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  emptyStateText?: string;
  showMainBadge?: boolean;
  showNumberBadge?: boolean;
}

const MAX_IMAGE_SIZE = 1280; // Max width/height in pixels (reduced for faster uploads)
const IMAGE_QUALITY = 0.75; // JPEG quality (0-1)
const MAX_FILE_SIZE_MB = 10; // Max file size in MB
const MAX_COMPRESSED_SIZE_KB = 500; // Target max compressed size in KB

const ImageUploader: React.FC<ImageUploaderProps> = ({
  images,
  onImagesChange,
  onRemoveImage,
  kind,
  minImages = 1,
  maxImages = 10,
  onAuthRequired,
  disabled = false,
  icon,
  emptyStateText,
  showMainBadge = false,
  showNumberBadge = false,
}) => {
  const { t } = useTranslation();
  const {
    containerNeutralLightMedium,
    textNeutralDefault,
    textNeutralMuted,
    accentColor,
  } = useColors();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string>("");
  const [draggedImageIndex, setDraggedImageIndex] = useState<number | null>(
    null,
  );
  const [dragOverImageIndex, setDragOverImageIndex] = useState<number | null>(
    null,
  );

  // Use uploadImage endpoint which uploads through backend (avoids CORS issues)
  const uploadImageMutation = trpcReact.uploadImage.useMutation();

  // Compress image using canvas and return as base64 string
  const compressImage = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;

          if (width > MAX_IMAGE_SIZE || height > MAX_IMAGE_SIZE) {
            if (width > height) {
              height = (height / width) * MAX_IMAGE_SIZE;
              width = MAX_IMAGE_SIZE;
            } else {
              width = (width / height) * MAX_IMAGE_SIZE;
              height = MAX_IMAGE_SIZE;
            }
          }

          // Create canvas and draw resized image
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Failed to get canvas context"));
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);

          // Progressive quality reduction to meet size target
          let quality = IMAGE_QUALITY;
          let base64 = canvas.toDataURL("image/jpeg", quality);

          // If still too large, reduce quality progressively
          while (
            base64.length > MAX_COMPRESSED_SIZE_KB * 1024 * 1.37 &&
            quality > 0.3
          ) {
            quality -= 0.1;
            base64 = canvas.toDataURL("image/jpeg", quality);
          }

          resolve(base64);
        };
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  }, []);

  // Upload a single image through the backend
  const uploadSingleImage = useCallback(
    async (file: File): Promise<UploadedImage | null> => {
      // Validate file size
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        throw new Error(
          `File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`,
        );
      }

      // Validate file extension
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const validExts = ["jpg", "jpeg", "webp", "png"];
      if (!validExts.includes(ext)) {
        throw new Error(
          `Invalid file type: ${ext}. Please use JPG, JPEG, WebP, or PNG.`,
        );
      }

      // Create temp URI for preview
      const tempUri = URL.createObjectURL(file);

      // Compress image to base64
      setUploadProgress("Compressing...");
      const base64Data = await compressImage(file);
      const compressedSize = Math.round((base64Data.length * 3) / 4 / 1024); // Approximate KB
      console.log(`Compressed image size: ~${compressedSize} KB`);

      // Upload through backend (avoids CORS issues with S3)
      setUploadProgress("Uploading...");
      const result = await uploadImageMutation.mutateAsync({
        data: base64Data,
        fileType: "jpg", // Always use jpg since we compress to JPEG
        kind: kind,
      });

      console.log("Upload successful:", result.object_name);

      return {
        uri: tempUri,
        object_name: result.object_name,
        get_url: result.get_url,
        progress: 1,
      };
    },
    [compressImage, uploadImageMutation, kind],
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      setUploadError(null);

      // Check if user is authenticated
      if (!user) {
        if (onAuthRequired) {
          onAuthRequired();
        }
        return;
      }

      // Check max images limit
      const remainingSlots = maxImages - images.length;
      if (remainingSlots <= 0) {
        setUploadError(`Maximum ${maxImages} images allowed.`);
        return;
      }

      const filesToProcess = Array.from(files).slice(0, remainingSlots);

      setUploading(true);
      const uploadedImages: UploadedImage[] = [];

      for (let i = 0; i < filesToProcess.length; i++) {
        const file = filesToProcess[i];
        try {
          setUploadProgress(
            `Processing ${i + 1}/${filesToProcess.length}: ${file.name}`,
          );
          const uploaded = await uploadSingleImage(file);
          if (uploaded) {
            uploadedImages.push(uploaded);
          }
        } catch (error: any) {
          console.error("Error uploading image:", error);
          setUploadError(
            error?.message || "Failed to upload image. Please try again.",
          );
        }
      }

      // Add all successfully uploaded images
      if (uploadedImages.length > 0) {
        onImagesChange((prev) => [...prev, ...uploadedImages]);
      }

      setUploading(false);
      setUploadProgress("");

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [
      user,
      onAuthRequired,
      maxImages,
      images.length,
      uploadSingleImage,
      onImagesChange,
    ],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled || !user) {
        if (!user && onAuthRequired) {
          onAuthRequired();
        }
        return;
      }

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const fakeEvent = {
          target: { files },
        } as React.ChangeEvent<HTMLInputElement>;
        await handleFileSelect(fakeEvent);
      }
    },
    [disabled, user, onAuthRequired, handleFileSelect],
  );

  const handleClick = useCallback(() => {
    if (disabled) return;
    if (!user && onAuthRequired) {
      onAuthRequired();
      return;
    }
    fileInputRef.current?.click();
  }, [disabled, user, onAuthRequired]);

  const reorderImages = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;
      onImagesChange((prev) => {
        if (
          fromIndex < 0 ||
          toIndex < 0 ||
          fromIndex >= prev.length ||
          toIndex >= prev.length
        ) {
          return prev;
        }
        const next = [...prev];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return next;
      });
    },
    [onImagesChange],
  );

  const handleImageDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    setDraggedImageIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleImageDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverImageIndex(index);
  };

  const handleImageDrop = (
    e: React.DragEvent<HTMLDivElement>,
    dropIndex: number,
  ) => {
    e.preventDefault();
    const fromIndex = draggedImageIndex;
    if (fromIndex === null) return;
    reorderImages(fromIndex, dropIndex);
    setDraggedImageIndex(null);
    setDragOverImageIndex(null);
  };

  const handleImageDragEnd = () => {
    setDraggedImageIndex(null);
    setDragOverImageIndex(null);
  };

  const defaultIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mx-auto mb-4"
      style={{ color: textNeutralMuted }}
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <circle cx="8.5" cy="8.5" r="1.5"></circle>
      <polyline points="21 15 16 10 5 21"></polyline>
    </svg>
  );

  return (
    <div className="space-y-4">
      {/* Auth warning */}
      {!user && onAuthRequired && (
        <div
          className="p-4 rounded-xl text-center"
          style={{ backgroundColor: `${accentColor}20` }}
        >
          <p style={{ color: textNeutralDefault }}>
            {t("signup.images.sign_in_required") || "Sign in to upload photos"}
          </p>
          <button
            onClick={onAuthRequired}
            className="mt-2 px-4 py-2 rounded-lg font-medium"
            style={{ backgroundColor: accentColor, color: "#fff" }}
          >
            {t("sign_in") || "Sign in"}
          </button>
        </div>
      )}

      {/* Upload area */}
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors hover:border-opacity-70 ${
          disabled || !user ? "opacity-50 pointer-events-none" : ""
        }`}
        style={{
          borderColor: textNeutralMuted,
          backgroundColor: `${containerNeutralLightMedium}50`,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/webp,image/png"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />
        {icon || defaultIcon}
        <p style={{ color: textNeutralDefault }}>
          {uploading
            ? uploadProgress || t("uploading") || "Uploading..."
            : emptyStateText ||
              t("click_or_drag") ||
              "Click or drag photos here"}
        </p>
        <p className="text-sm mt-1" style={{ color: textNeutralMuted }}>
          JPG, JPEG, WebP, PNG
        </p>
      </div>

      {/* Error message */}
      {uploadError && (
        <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm">
          {uploadError}
        </div>
      )}

      {/* Image grid */}
      {images.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs" style={{ color: textNeutralMuted }}>
            {t("reorder_photos_hint") ||
              "Drag photos to reorder them. First photo is used as the main image."}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((image, index) => (
              <div
                key={`${image.object_name}-${index}`}
                className={`relative aspect-square rounded-xl overflow-hidden group ${
                  !disabled && image.progress === 1
                    ? "cursor-grab active:cursor-grabbing"
                    : ""
                }`}
                draggable={!disabled && image.progress === 1}
                onDragStart={(e) => handleImageDragStart(e, index)}
                onDragOver={(e) => handleImageDragOver(e, index)}
                onDrop={(e) => handleImageDrop(e, index)}
                onDragEnd={handleImageDragEnd}
                style={{
                  outline:
                    dragOverImageIndex === index
                      ? `2px dashed ${accentColor}`
                      : undefined,
                  outlineOffset: dragOverImageIndex === index ? -4 : undefined,
                  opacity: draggedImageIndex === index ? 0.7 : 1,
                }}
              >
                <img
                  src={image.get_url || image.uri}
                  alt={`Image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                {/* Upload progress overlay */}
                {image.progress < 1 && (
                  <div
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                  >
                    <div className="w-12 h-12 rounded-full border-4 border-white border-t-transparent animate-spin" />
                  </div>
                )}
                {/* Remove button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveImage(index);
                  }}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
                {/* Main photo badge */}
                {showMainBadge && index === 0 && (
                  <div
                    className="absolute bottom-2 left-2 px-2 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: accentColor, color: "white" }}
                  >
                    {t("signup.images.main_photo") || "Main"}
                  </div>
                )}
                {/* Number badge */}
                {showNumberBadge && (
                  <div
                    className="absolute bottom-2 left-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium"
                    style={{ backgroundColor: accentColor, color: "white" }}
                  >
                    {index + 1}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hint for more images */}
      {images.length > 0 && images.length < minImages && (
        <p className="text-sm" style={{ color: textNeutralMuted }}>
          {t("add_more_photos") || "Add"} {minImages - images.length}{" "}
          {t("more_photos") || "more photo(s)"}
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
